import { PredictionResults } from '@models/Prediction/Prediction'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Tippy, { useSingleton } from '@tippyjs/react'
import { ToolTipBox } from '@components/ToolTip/ToolTipBox'
import { ToolTipContent } from '@components/ToolTip/ToolTipContent'
import { Icon } from '@components/Icon/Icon'
import ModalDialog from '@components/ModalDialog/ModalDialog'
import { ResultsChartView } from '@components/Survey/TabbedPane/ResultsChartView'
import { PredictionContext } from '@context/PredictionContext'
import { SurveyContext } from '@context/SurveyContext'
function usePredictionBarData(predictionResults?: PredictionResults) {
  const {
    i18n: { language },
  } = useTranslation()
  const currentLocale = (language ?? 'fr').substring(0, 2)

  const predictionData = useMemo(() => {
    const data = predictionResults?.prediction?.data ?? []
    const sortedData = [...data].sort((a, b) => b.value - a.value)

    // Surveys categories with icons take more space, we need limit them to 3 so the graphic don't go off-screen
    const hasIcon = sortedData.every((item) => item.icon)
    const categoriesLimit = hasIcon ? 3 : 5

    if (sortedData.length < categoriesLimit) {
      return sortedData
    }

    return sortedData.slice(0, categoriesLimit)
  }, [predictionResults])

  const formatPercent = useCallback(
    (val: number) => new Intl.NumberFormat('default', { style: 'percent', maximumFractionDigits: 2 }).format(val),
    [],
  )

  return { currentLocale, predictionData, formatPercent }
}

function PredictionBarsStatic({ predictionResults }: { predictionResults?: PredictionResults }) {
  const { currentLocale, predictionData, formatPercent } = usePredictionBarData(predictionResults)

  return (
    <div className="TabbedPane-body-navigation-predictions">
      {predictionData.map(({ names, value, icon, color, id }) => (
        <div key={id} className="TabbedPane-body-navigation-predictions-Prediction">
          {icon && <Icon icon={icon} />}
          <div className="box">
            <p style={{ color }}>{names.acr[currentLocale]}</p>
            <span>{formatPercent(value)}</span>
          </div>
        </div>
      ))}
      <div className="TabbedPane-body-navigation-predictions-Bars">
        {predictionData.map(({ value, color, id }) => (
          <span
            key={id}
            style={{
              height: `${value * 100}%`,
              width: '10px',
              backgroundColor: color,
            }}
          />
        ))}
      </div>
    </div>
  )
}

function tippyAppBoundary(): HTMLElement {
  return (document.querySelector('.AppLayout') as HTMLElement | null) ?? document.body
}

function PredictionBarsInteractive({ predictionResults }: { predictionResults?: PredictionResults }) {
  const { t } = useTranslation('EditionProgress')
  const { currentLocale, predictionData, formatPercent } = usePredictionBarData(predictionResults)
  const [source, target] = useSingleton()
  const [modalOpen, setModalOpen] = useState(false)
  const { dsmpQ1, dsmpQ2, results, sandboxedResults } = useContext(PredictionContext)
  const { resultVariant } = useContext(SurveyContext)

  const snapshot = predictionResults ?? sandboxedResults ?? results
  const canOpenModal = !!snapshot?.prediction

  const openModal = useCallback(() => {
    if (canOpenModal) {
      setModalOpen(true)
    }
  }, [canOpenModal])

  const chartTitle = resultVariant === 'intermediate' ? t('resultsIntermediate') : t('resultsFinal')

  return (
    <>
      <Tippy
        delay={200}
        duration={1000}
        animation={false}
        touch={true}
        placement="bottom"
        appendTo={tippyAppBoundary}
        // Always below the sticky nav, clamped inside the central app column.
        popperOptions={{
          strategy: 'absolute',
          modifiers: [
            { name: 'flip', enabled: false },
            {
              name: 'preventOverflow',
              options: {
                boundary: tippyAppBoundary(),
                mainAxis: false,
                altAxis: true,
                tether: false,
                padding: 8,
              },
            },
            { name: 'offset', options: { offset: [0, 6] } },
          ],
        }}
        render={(attr, content) => {
          attr['data-placement'] = 'bottom'
          return (
            <ToolTipBox numberOfColumns={predictionData.some((p) => p.icon) ? 3 : 2} {...attr}>
              {content}
            </ToolTipBox>
          )
        }}
        singleton={source}
      />
      <div
        className={[
          'TabbedPane-body-navigation-predictions',
          canOpenModal ? 'TabbedPane-body-navigation-predictions--clickable' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        id="PredictionBar"
        role={canOpenModal ? 'button' : undefined}
        tabIndex={canOpenModal ? 0 : undefined}
        aria-label={canOpenModal ? t('openChart') : undefined}
        onClick={canOpenModal ? openModal : undefined}
        onKeyDown={
          canOpenModal
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openModal()
                }
              }
            : undefined
        }
      >
        {predictionData.map(({ names, value, icon, color, id }) => (
          <Tippy
            key={id}
            touch={true}
            placement="bottom"
            content={
              <ToolTipContent icon={icon} content={names.full[currentLocale]} percentage={formatPercent(value)} />
            }
            singleton={target}>
            <div className="TabbedPane-body-navigation-predictions-Prediction">
              {icon && <Icon icon={icon} />}
              <div className="box">
                <p style={{ color }}>{names.acr[currentLocale]}</p>
                <span>{formatPercent(value)}</span>
              </div>
            </div>
          </Tippy>
        ))}
        <div className="TabbedPane-body-navigation-predictions-Bars">
          {predictionData.map(({ value, color, id }) => (
            <span
              key={id}
              style={{
                height: `${value * 100}%`,
                width: '10px',
                backgroundColor: color,
              }}
            />
          ))}
        </div>
      </div>
      <ModalDialog
        visible={modalOpen && !!snapshot}
        title={chartTitle}
        bodyComponent={
          snapshot ? (
            <div className="EditionProgressBar-modalBody">
              <div className="EditionProgressBar-modalBody-media">
                <ResultsChartView
                  snapshot={snapshot}
                  resultVariant={resultVariant ?? 'final'}
                  dsmpQ1={dsmpQ1}
                  dsmpQ2={dsmpQ2}
                />
              </div>
            </div>
          ) : null
        }
        onClose={() => setModalOpen(false)}
        extraClass="EditionProgressBar-modal"
      />
    </>
  )
}

export function PredictionBars({
  predictionResults,
  /** When false, render sticky mini-bars without nested tippies (e.g. inside progress tooltip). */
  interactive = true,
}: {
  predictionResults?: PredictionResults
  interactive?: boolean
}) {
  if (!interactive) {
    return <PredictionBarsStatic predictionResults={predictionResults} />
  }
  return <PredictionBarsInteractive predictionResults={predictionResults} />
}
